import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';

interface AdminProtectedRouteProps {
  children: ReactNode;
}

/**
 * AdminProtectedRoute - Guards admin-only routes
 *
 * Requires:
 * - User to be authenticated
 * - User profile to have role = 'admin'
 *
 * Does NOT require:
 * - Onboarding completion
 * - MFA (since admin routes are internal tooling)
 */
export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { user, profile, loading } = useAuth();

  // Show loading while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not logged in - redirect to auth
  if (!user) {
    logger.log('[AdminProtectedRoute] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Waiting for profile to load
  if (!profile) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check admin role
  if (profile.role !== 'admin') {
    logger.log('[AdminProtectedRoute] User is not admin, redirecting to /dashboard', {
      userId: user.id.substring(0, 8),
      role: profile.role,
    });
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and is admin
  logger.log('[AdminProtectedRoute] Admin access granted', {
    userId: user.id.substring(0, 8),
  });

  return <>{children}</>;
}

export default AdminProtectedRoute;
